import type { AssessedRiskRating, RiskRatingHistoryEntry } from '@gcv/shared';
import { RiskRatingSource } from '@gcv/shared';

import { ExtendedRiskRatingSource, RISK_RATING_SOURCE_LABELS } from 'ui/apps/fi/shared/types';

export interface RatingHistoryRow {
  id: string;
  /** ISO timestamp of the change. */
  date: string;
  /** Undefined on the earliest row - there is no prior rating to change from. */
  fromRating?: AssessedRiskRating;
  toRating: AssessedRiskRating;
  score?: number;
  source: string;
  /** The assessment or review behind the change, where one applies. */
  sourceName?: string;
  overridden: boolean;
  /** The override that held back this change; see `wouldBe*`. */
  suppressedBy?: string;
  wouldBeFromRating?: AssessedRiskRating;
  wouldBeToRating?: AssessedRiskRating;
  /** The backfill's starting row. Nothing behind it to open, so its View is disabled. */
  isSeeded: boolean;
  justification?: string;
  byUserId?: string;
  /** Resolved server-side; preferred over byUserId where the API supplies it. */
  byName?: string;
  /** Absent for a manual rating. */
  resultId?: string;
}

/**
 * The account's rating history as table rows, newest first. Recorded per change rather than
 * derived from assessments, so `previous_rating` comes from the entry, not the preceding row.
 */
export const buildRatingHistoryRows = (history?: RiskRatingHistoryEntry[]): RatingHistoryRow[] =>
  (history ?? [])
    .map((entry) => {
      const source = entry.source as RiskRatingSource | ExtendedRiskRatingSource;

      return {
        id: entry.id,
        date: entry.changed_on,
        fromRating: entry.previous_rating,
        toRating: entry.new_rating,
        score: entry.score,
        // An unlabelled source stays blank: naming it would assert a provenance we do not know.
        source: RISK_RATING_SOURCE_LABELS[source] ?? '',
        sourceName: entry.source_name,
        overridden: source === RiskRatingSource.ManualOverride,
        suppressedBy: entry.suppressed_by_source
          ? RISK_RATING_SOURCE_LABELS[entry.suppressed_by_source]
          : undefined,
        wouldBeFromRating: entry.would_be_previous_rating,
        wouldBeToRating: entry.would_be_new_rating,
        isSeeded: source === ExtendedRiskRatingSource.Seeded,
        justification: entry.justification,
        byUserId: entry.changed_by_user_id,
        byName: entry.changed_by_name,
        resultId: entry.result_id
      };
    })
    .sort((a, b) => b.date.localeCompare(a.date));
